/* eslint-disable jsx-a11y/alt-text */
'use client'

import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { OrganizationMember } from '@skop-app/lib-domain/types'
import { currencies } from '@skop-app/lib-domain/constants'
import { parseSaleCurrency } from '@skop-app/lib-domain/shop'

import { firstPdfPageItems, formatCurrency, splitItemsPerPage } from '@/utils'
import { getOrderItemDimensionsLabel } from '@/features/quote/lib/orderProducts'
import { formatAddress, invoiceNumberGenerator } from '@/utils/helpers'
import { OrderModalValues, SignDocumentTabOptions } from '@/types'

import { Footer } from '../PDFFooter/pdfFooter.organism'
import { Table } from '../PDFTable/pdfTable.organism'
import { PDFSignPage } from '../PDFSignPage/pdfSignPage.organism'
import { styles } from '../pdfStyles'

type PDFInvoiceProps = {
  order: Omit<OrderModalValues, 'currency'> & {
    currency?: OrderModalValues['currency'] | string
  }
  enableTax?: boolean
  invoiceAsQuote?: boolean
  teamLogo?: string
  options?: SignDocumentTabOptions
  manager?: OrganizationMember
}

export const PDFInvoice = ({
  order: sourceOrder,
  enableTax = true,
  invoiceAsQuote = false,
  teamLogo,
  options,
  manager,
}: PDFInvoiceProps) => {
  const sourceCurrency = sourceOrder?.currency
  const currencyCode =
    parseSaleCurrency(
      typeof sourceCurrency === 'string'
        ? sourceCurrency
        : sourceCurrency?.value,
    ) ?? 'EUR'
  const currencySign =
    (typeof sourceCurrency === 'object' ? sourceCurrency?.sign : undefined) ??
    currencies.find((item) => String(item.value).toUpperCase() === currencyCode)
      ?.sign ??
    currencyCode
  const order: OrderModalValues = {
    ...sourceOrder,
    currency: {
      label: currencyCode,
      value: currencyCode.toLowerCase(),
      sign: currencySign,
    },
  }
  const {
    id,
    date,
    items,
    shop,
    subTotal,
    vat,
    vatPercentage,
    total,
    email,
    name,
    SIRET,
    company,
    address,
    city,
    country,
    zipcode,
    orderParameters,
    orderBonusFieldOne,
    orderBonusFieldTwo,
    billingAddress,
    deliveryAddress,
    customer,
    phone,
  } = order || {}

  const invoiceNumber = invoiceNumberGenerator(id)
  const saleConditionsExist =
    orderParameters?.showSaleConditions && orderParameters?.saleConditions
  const showSignAndConditions =
    orderParameters?.showOnInvoices || !orderParameters?.hideSignInputs

  const pages = splitItemsPerPage({
    items: items || [],
    firstPageItemsLength: invoiceAsQuote
      ? firstPdfPageItems({
          itemsLength: items?.length || 0,
          showSignAndConditions,
        })
      : 17,
    otherPagesItemsLength: 28,
    lastPageItemsLength: invoiceAsQuote
      ? showSignAndConditions
        ? 18
        : 25
      : 25,
  })

  const timeRemovedDate = date?.split(' ')?.[0]
  const formatedDate = (date?: string) => {
    return format(parseISO(date || new Date().toISOString()), 'dd/MM/yyyy', {
      locale: fr,
    })
  }

  const isCompany = customer?.type === 'COMPANY'

  return (
    <Document>
      {/* Invoice or InvoiceQuote pages */}
      {pages.map((page: any, pageIdx: number) => {
        return (
          <Page size="A4" style={styles.container} key={`page-${pageIdx}`}>
            {/* BODY */}
            <View>
              {/* HEADER */}
              {pageIdx === 0 && (
                <>
                  <View style={styles.header}>
                    {!!(teamLogo || order?.shop?.logo) && (
                      <View
                        style={{
                          width: '48%',
                        }}
                      >
                        <View style={styles.logoContainer}>
                          <Image
                            style={styles.logoImage}
                            src={teamLogo || order?.shop?.logo}
                          />
                        </View>
                      </View>
                    )}
                    <View style={{ width: '50%' }}>
                      <Text style={styles.title}>
                        {invoiceAsQuote ? 'Devis' : 'Facture'}{' '}
                      </Text>
                      <View style={styles.rowLine}>
                        <Text>
                          Numéro de {invoiceAsQuote ? 'devis' : 'facture'}: #
                          {invoiceNumber}
                        </Text>
                      </View>
                      {!invoiceAsQuote && (
                        <View style={styles.rowLine}>
                          <Text>Date de la commande: {timeRemovedDate}</Text>
                        </View>
                      )}
                      <View style={styles.rowLine}>
                        <Text>
                          Date d&apos;émission: {formatedDate(order?.issueDate)}
                        </Text>
                      </View>
                      {!!invoiceAsQuote && order?.requestType === 'quote' && (
                        <View style={styles.rowLine}>
                          <Text>
                            Date d&apos;expiration:{' '}
                            {formatedDate(order?.expireDate)}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                  {/* Skop number + Facturer à */}
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginBottom: 48,
                      borderTop: '1.4px solid black',
                      paddingTop: 12,
                    }}
                  >
                    <View style={{ width: '50%' }}>
                      <Text style={styles.bold}>
                        {invoiceAsQuote ? 'Émetteur' : 'Facturé par'}
                      </Text>
                      {!!invoiceAsQuote && (
                        <>
                          <Text style={styles.shopDetails}>
                            {manager?.name || shop?.user?.name}
                          </Text>
                          <Text style={styles.shopDetails}>
                            {manager?.email || shop?.user?.email}
                          </Text>
                        </>
                      )}
                      {!!invoiceAsQuote && (
                        <Text style={{ ...styles.bold, marginTop: 10 }}>
                          Entreprise
                        </Text>
                      )}
                      {!!(shop?.organization?.name || shop?.name) && (
                        <Text
                          style={{
                            ...styles.shopDetails,
                            fontSize: 10,
                          }}
                        >
                          {shop?.organization?.name || shop?.name}
                        </Text>
                      )}
                      {!!(shop?.organization?.email || shop?.contacts[0]) && (
                        <Text style={styles.shopDetails}>
                          {shop?.organization?.email || shop?.contacts[0]}
                        </Text>
                      )}
                      {!!(shop?.organization?.phone || shop?.phone) && (
                        <Text style={styles.shopDetails}>
                          {shop?.organization?.phone || shop?.phone}
                        </Text>
                      )}
                      {!!shop?.organization?.SIRET && (
                        <Text style={styles.shopDetails}>
                          SIRET: {shop?.organization?.SIRET}
                        </Text>
                      )}
                      <Text style={styles.shopDetails}>
                        {formatAddress({
                          address: shop?.organization?.address,
                          city: shop?.organization?.city,
                          zipcode: shop?.organization?.zipcode,
                          country: shop?.organization?.country,
                          separatedLines: true,
                        })}
                      </Text>
                      {!!orderBonusFieldOne && (
                        <View>
                          <View
                            style={{
                              ...styles.rowLine,
                              marginTop: 20,
                              marginBottom: 0,
                            }}
                          >
                            <Text
                              style={{
                                ...styles.bold,
                                fontSize: 10,
                              }}
                            >
                              {orderBonusFieldOne}
                            </Text>
                          </View>
                          <View
                            style={{
                              ...styles.rowLine,
                              marginTop: 4,
                              marginBottom: 0,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 6,
                                color: '#666666',
                                fontFamily: 'Helvetica',
                              }}
                            >
                              {orderBonusFieldTwo}
                            </Text>
                          </View>
                        </View>
                      )}
                    </View>
                    <View style={{ width: '50%' }}>
                      <Text style={styles.bold}>
                        {invoiceAsQuote ? 'Client' : 'Facturé à'}
                      </Text>
                      {!!(invoiceAsQuote || !isCompany) && (
                        <>
                          <Text style={styles.shopDetails}>
                            {billingAddress?.name || name}
                          </Text>
                          <Text style={styles.shopDetails}>
                            {billingAddress?.email || customer?.email || email}
                          </Text>
                        </>
                      )}
                      {!!(invoiceAsQuote && isCompany) && (
                        <Text style={{ ...styles.bold, marginTop: 10 }}>
                          Entreprise
                        </Text>
                      )}
                      {!!(company || customer?.companyName) && (
                        <Text style={styles.shopDetails}>
                          {company || customer?.companyName}
                        </Text>
                      )}
                      {!!(isCompany && !invoiceAsQuote) && (
                        <Text style={styles.shopDetails}>
                          {billingAddress?.email || customer?.email}
                        </Text>
                      )}
                      {!!(
                        phone ||
                        customer?.phone ||
                        billingAddress?.phone ||
                        deliveryAddress?.phone
                      ) && (
                        <Text style={styles.shopDetails}>
                          {phone ||
                            customer?.phone ||
                            billingAddress?.phone ||
                            deliveryAddress?.phone}
                        </Text>
                      )}
                      {!!(SIRET || customer?.SIRET) && (
                        <Text style={styles.shopDetails}>
                          SIRET : {SIRET || customer?.SIRET}
                        </Text>
                      )}
                      {!!customer?.VAT && (
                        <Text style={styles.shopDetails}>
                          TVA : {customer?.VAT}
                        </Text>
                      )}
                      <Text style={styles.shopDetails}>
                        {formatAddress({
                          address:
                            billingAddress?.address ||
                            deliveryAddress?.address ||
                            address,
                          city:
                            billingAddress?.city ||
                            deliveryAddress?.city ||
                            city,
                          zipcode:
                            billingAddress?.zipcode ||
                            deliveryAddress?.zipcode ||
                            zipcode,
                          country:
                            billingAddress?.country ||
                            deliveryAddress?.country ||
                            country,
                          separatedLines: true,
                        })}
                      </Text>
                    </View>
                  </View>
                </>
              )}

              {/* Table headers */}
              <Table.Root>
                <Table.Header
                  headers={[
                    { label: 'Description', style: { width: '50%' } },
                    { label: 'Qté', style: { width: '15%' } },
                    { label: 'Unité', style: { width: '15%' } },
                    { label: 'Prix u. HT', style: { width: '18%' } },
                    {
                      label: {
                        labelTitle: 'Total HT',
                        labelStyle: { textAlign: 'right' },
                      },
                      style: { width: '17%' },
                    },
                  ]}
                />
                <Table.Line />
                {page.map((item, idx: number) => {
                  const isService = item.rawProduct?.nature === 'service'
                  const subtitleParts = [
                    getOrderItemDimensionsLabel(item),
                    isService ? '(Service)' : undefined,
                  ].filter((part): part is string => Boolean(part))

                  return (
                    <View key={item?.id}>
                      <Table.Row
                        key={item?.id}
                        rowItems={[
                          {
                            label: {
                              labelTitle: item.name,
                              labelSubtitle: subtitleParts.join(' - '),
                            },
                            rowStyle: { width: '51%' },
                          },
                          {
                            label: item.quantity,
                            rowStyle: { width: '15%' },
                          },
                          {
                            label: item.unitLabel || item.unit,
                            rowStyle: { width: '15%' },
                          },
                          {
                            label: formatCurrency(
                              item?.rawPrice || item?.price || 0,
                              currencySign,
                            ),
                            rowStyle: { width: '18%' },
                          },
                          {
                            label: {
                              labelTitle: formatCurrency(
                                item?.rawTotal || item?.total || 0,
                                currencySign,
                              ),
                              labelStyle: { textAlign: 'right' },
                            },
                            rowStyle: { width: '17%' },
                          },
                        ]}
                        lastRow={idx === page.length - 1}
                      />
                      {pages.length - 1 === pageIdx &&
                        page.length - 1 === idx && <Table.Line />}
                    </View>
                  )
                })}
              </Table.Root>

              {/* Total */}
              {pageIdx === pages.length - 1 && (
                <>
                  <View style={styles.flex}>
                    <View style={{ width: '50%' }} />
                    <View style={{ width: '50%' }}>
                      {enableTax ? (
                        <>
                          <View style={styles.totalLine}>
                            <Text>Montant Total HT</Text>
                            <Text>
                              {formatCurrency(subTotal, currencySign)}
                            </Text>
                          </View>
                          <View style={styles.totalLine}>
                            <Text>
                              TVA
                              {vatPercentage?.value != null
                                ? ` (${vatPercentage.value} %)`
                                : ''}{' '}
                            </Text>
                            <Text>{formatCurrency(vat, currencySign)}</Text>
                          </View>
                          <View style={styles.totalLine}>
                            <Text style={styles.bold}>Montant Total TTC</Text>
                            <Text style={styles.bold}>
                              {formatCurrency(total, currencySign)}
                            </Text>
                          </View>
                        </>
                      ) : (
                        <View
                          style={{
                            borderTop: '0.24px solid #c5c4c4',
                            padding: '6 0',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text style={styles.bold}>Total</Text>
                          <Text style={styles.bold}>
                            {formatCurrency(subTotal, currencySign)}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                </>
              )}
            </View>
            <Footer
              order={order}
              showSignAndConditions={
                !!(showSignAndConditions && pageIdx === pages.length - 1)
              }
              label={
                invoiceAsQuote ? 'Devis généré par' : 'Facture générée par'
              }
              currentPage={pageIdx + 1}
              totalPages={pages.length + (saleConditionsExist ? 1 : 0)}
            />
          </Page>
        )
      })}

      {/* Additional order condition page */}
      {!!(
        invoiceAsQuote &&
        saleConditionsExist &&
        !options?.hideSelectSignDocument
      ) && (
        <PDFSignPage
          title={orderParameters?.saleConditionsLabel || ''}
          description={orderParameters?.saleConditions || ''}
          order={order}
          currentPage={pages.length + 1}
          totalPages={pages.length + 1}
          signPage={false}
          manager={manager}
        />
      )}
    </Document>
  )
}
